import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Building2, Copy, CheckCircle } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { SEO } from '../shared/SEO';

export function PaymentPage() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const { invoices } = useApp();
  const [invoice, setInvoice] = useState<{
    id: string;
    invoiceNumber: string;
    client: {
      name: string;
      email: string;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const amount = parseFloat(searchParams.get('amount') || '0');
  const currency = searchParams.get('currency') || 'QAR';

  useEffect(() => {
    if (invoiceId) {
      const foundInvoice = invoices.find(inv => inv.id === invoiceId);
      if (foundInvoice) {
        setInvoice({
          id: foundInvoice.id,
          invoiceNumber: foundInvoice.invoiceNumber,
          client: {
            name: foundInvoice.client.name,
            email: foundInvoice.client.email
          }
        });
      } else {
        setInvoice(null);
      }
      setLoading(false);
    }
  }, [invoiceId, invoices]);

  const handleCopyClick = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h2>
          <p className="text-gray-600">The invoice you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const pageTitle = invoice ? `Pay Invoice #${invoice.invoiceNumber}` : 'Pay Invoice';
  const pageDescription = invoice ? `Pay invoice #${invoice.invoiceNumber} for ${invoice.client.name} securely via FreelancePay.` : 'Pay your invoice securely via FreelancePay.';

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={["pay invoice", "payment", "freelance", "FreelancePay"]}
      />
      <div className="min-h-screen bg-gray-100 py-12">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-6 w-6 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Bank Transfer Payment</h1>
              <p className="text-gray-600">Pay your invoice via bank transfer</p>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Payment Details</h3>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Invoice {invoice.invoiceNumber}</span>
                  <span className="text-xl font-bold text-gray-900">
                    {currency} {amount.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Bank Account Details</h3>
                
                <div className="space-y-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500">Bank Name</p>
                        <p className="font-medium">Qatar National Bank</p>
                      </div>
                      <button
                        onClick={() => handleCopyClick('Qatar National Bank')}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500">Account Number</p>
                        <p className="font-medium">0123456789</p>
                      </div>
                      <button
                        onClick={() => handleCopyClick('0123456789')}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500">IBAN</p>
                        <p className="font-medium">QA12QNBA0123456789</p>
                      </div>
                      <button
                        onClick={() => handleCopyClick('QA12QNBA0123456789')}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500">SWIFT/BIC</p>
                        <p className="font-medium">QNBAQAQA</p>
                      </div>
                      <button
                        onClick={() => handleCopyClick('QNBAQAQA')}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Payment Instructions</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                    <li>Use your invoice number ({invoice.invoiceNumber}) as the payment reference</li>
                    <li>Transfer the exact amount: {currency} {amount.toFixed(2)}</li>
                    <li>Send the payment confirmation to {invoice.client.email}</li>
                    <li>Allow 1-3 business days for the payment to be processed</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-sm text-gray-500">
              <p>Need help? Contact our support team</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}